import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { Checkbox, Spin } from 'antd';
import styled, { createGlobalStyle, css } from 'styled-components';

import { Colors, Languages, RESUME_FIELDS_RU } from 'context';
import { ToastTypes } from 'atoms';
import { CvLanguagePicker, ResumeDownloadModal } from 'molecules';
import {
  CareerProfileForm,
  CareerProfileShowcase,
  CV_SECTIONS,
  CV_TEXT_LANGUAGES,
  CvSectionId,
  CvTextBlock,
  ICareerProfileFormValues,
} from 'organisms';

import {
  useCareerJourney,
  useMyCareerCenter,
  getCareerProfileErrorText,
  ICareerProfileData,
  useCareerProfile,
  useDownloadCv,
  useSaveCareerProfile,
  useSetProfileSearchable,
  useToaster,
} from 'core';
import { careerColor, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';
import { mobile } from 'services/ui';

import { emptyCareerProfileValues } from './constants';
import { useUnsavedChangesGuard } from './model/useUnsavedChangesGuard';
import { CvCompleteness } from './parts/CvCompleteness';
import { CvPhotoField } from './parts/CvPhotoField';

// Polish unconditionally — it is the market, and Russian is no longer offered.
const DEFAULT_LANGUAGE = Languages.polish;

/** Две колонки «форма | превью» — от этой ширины окна (ТЗ 0072, 2.1). */
const wide = '@media (min-width: 1280px)';

type Tab = 'cv' | 'visibility';
type Pane = 'edit' | 'preview';

const sameText = (
  a?: { jobTitle: string; about: string },
  b?: { jobTitle: string; about: string },
) => (a?.jobTitle ?? '') === (b?.jobTitle ?? '') && (a?.about ?? '') === (b?.about ?? '');

/**
 * `/career/profile` — рабочий экран CV (spec 0018 B; переработка 0072, шаг 2).
 *
 * Аудит в Brave: перед редактором стояли портрет, чек-лист, полоса полноты,
 * согласие на видимость и большая секция фото — до формы надо было долистать,
 * а сохранялось всё тремя разными кнопками. Теперь:
 *
 * 1. заголовок и две вкладки — «CV» и «Widoczność profilu»;
 * 2. закреплённая панель: язык документа, состояние, «Zapisz zmiany», «Pobierz PDF»;
 * 3. одна строка заполненности и навигация по разделам;
 * 4. форма слева, превью справа (от 1280 px; ниже — переключатель).
 *
 * 🔴 Одна команда сохранения = одна мутация `saveCareerProfile` (профиль, фото
 * и изменённые тексты CV одной транзакцией на сервере). Частично сохранённого
 * состояния не бывает: либо «Zapisano», либо черновик остаётся в форме с
 * пометкой «Nie zapisano».
 */
export const Resume = memo(() => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { profile, loading } = useCareerProfile();
  const [saveProfile, { loading: saving, errorCode, errorField, savedProfile }] =
    useSaveCareerProfile();
  const [setSearchable] = useSetProfileSearchable();
  const [downloadCv, { loading: downloadLoading }] = useDownloadCv();
  const { addToast } = useToaster();
  const { isPreview } = useMyCareerCenter();
  // Проверки резюме — с сервера, те же семь, что этап «CV» на главной.
  const { journey } = useCareerJourney();

  const tab: Tab = searchParams.get('tab') === 'visibility' && !isPreview ? 'visibility' : 'cv';
  const [language, setLanguage] = useState<Languages>(DEFAULT_LANGUAGE);
  const [pane, setPane] = useState<Pane>('edit');
  const [failed, setFailed] = useState(false);

  const [isSearchable, setIsSearchable] = useState(false);
  const [contactsVisible, setContactsVisible] = useState(false);
  // Profile photo edit state: `null` = untouched (show the server photo), a File
  // = a new upload to send, '' = the user cleared the existing photo. Kept out of
  // Formik so the enableReinitialize dance below never resets it.
  const [photoValue, setPhotoValue] = useState<File | '' | null>(null);
  /**
   * Превью только что выбранного фото.
   *
   * 🔴 `createObjectURL` НЕЛЬЗЯ звать в теле рендера: каждый ре-рендер создавал
   * бы новую blob-ссылку, старая жила бы до закрытия вкладки, а `src` менял бы
   * идентичность — фото заметно дёргалось. Считаем один раз на файл и
   * освобождаем предыдущую.
   */
  const localPhotoUrl = useMemo(
    () => (photoValue instanceof File ? URL.createObjectURL(photoValue) : ''),
    [photoValue],
  );
  useEffect(
    () => () => {
      if (localPhotoUrl) URL.revokeObjectURL(localPhotoUrl);
    },
    [localPhotoUrl],
  );

  // Reinitialize the form from server data ONLY on the first load. After that,
  // background refetches (e.g. the save-response cache write) must not reinitialize
  // Formik, or edits typed while a save is round-tripping would be silently wiped.
  const hasLoadedOnce = useRef(false);
  // The exact values object passed to the last save — used to tell whether the
  // user has typed anything since pressing Save.
  const submittedValuesRef = useRef<ICareerProfileFormValues | null>(null);
  // Guards the save-response reset so it runs once per new mutation result.
  const appliedSaveRef = useRef<ICareerProfileData | null>(null);

  const formik = useFormik<ICareerProfileFormValues>({
    initialValues: profile?.values ?? emptyCareerProfileValues,
    enableReinitialize: !hasLoadedOnce.current,
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (profile) {
      hasLoadedOnce.current = true;
      setIsSearchable(profile.isSearchable);
      setContactsVisible(profile.contactsVisible);
    }
  }, [profile]);

  // Adopt the saved response as the new baseline (fresh certificate ids, texts),
  // but ONLY if the user has not typed since submitting — otherwise resetting
  // would discard edits made while the save was in flight.
  useEffect(() => {
    if (!savedProfile || savedProfile === appliedSaveRef.current) {
      return;
    }
    appliedSaveRef.current = savedProfile;
    // The server now holds the photo; drop the pending edit so the next save does
    // not re-upload/re-clear, and let the preview fall back to the fresh photoUrl.
    setPhotoValue(null);
    if (formik.values === submittedValuesRef.current) {
      formik.resetForm({ values: savedProfile.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProfile]);

  useEffect(() => {
    if (errorCode) {
      // Текст по КОДУ: сообщение сервера написано по-русски.
      addToast('save_profile', {
        type: ToastTypes.danger,
        text: getCareerProfileErrorText(t, errorCode, errorField),
      });
    }
  }, [errorCode, errorField, addToast, t]);

  const dirty = formik.dirty || photoValue !== null;
  useUnsavedChangesGuard(dirty && !isPreview, t('careerProfile.unsavedConfirm'));

  // Новая правка после неудачи — это уже новый черновик, а не «не сохранено».
  useEffect(() => {
    setFailed(false);
  }, [formik.values, photoValue]);

  const { values: formValues, initialValues } = formik;
  const saveAll = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true;
    const values = formValues;
    submittedValuesRef.current = values;
    const changedLanguages = CV_TEXT_LANGUAGES.filter(
      (code) => !sameText(values.cvTexts[code], initialValues.cvTexts[code]),
    );
    const ok = await saveProfile(
      values,
      photoValue instanceof File ? photoValue : undefined,
      photoValue === '',
      changedLanguages,
    );
    // После `await` эффект сброса формы ещё не отработал: флаг ставим после.
    setTimeout(() => setFailed(!ok), 0);
    return ok;
  }, [dirty, formValues, initialValues, photoValue, saveProfile]);

  /**
   * PDF строится из СОХРАНЁННОЙ версии на сервере. С несохранёнными правками
   * кнопка сначала сохраняет, и только после успеха скачивает; при ошибке
   * экспорт не запускается — иначе человек отправил бы работодателю старое CV.
   */
  const download = async () => {
    if (dirty && !(await saveAll())) {
      addToast('download_cv', {
        type: ToastTypes.danger,
        text: t('careerProfile.download.saveFailed'),
      });
      return;
    }
    downloadCv(language);
  };

  const jumpTo = useCallback((id: CvSectionId) => {
    setPane('edit');
    // После смены панели раздел должен уже быть видим, иначе прокрутка — в никуда.
    window.setTimeout(() => {
      const section = document.getElementById(id);
      if (!section) return;
      section.scrollIntoView({ block: 'start' });
      const heading = section.querySelector<HTMLElement>('h2, h3');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }, 0);
  }, []);

  // `/career/profile#cv-projects` (действия плана «портфолио» и «CV») — сразу к разделу.
  const jumpedHash = useRef('');
  useEffect(() => {
    const id = hash.replace('#', '') as CvSectionId;
    if (!profile || !id || jumpedHash.current === id) return;
    if (!CV_SECTIONS.some((section) => section.id === id)) return;
    jumpedHash.current = id;
    jumpTo(id);
  }, [hash, profile, jumpTo]);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'cv') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const onToggleSearchable = (checked: boolean) => {
    setIsSearchable(checked);
    const nextContacts = checked ? contactsVisible : false;
    if (!checked) {
      setContactsVisible(false);
    }
    setSearchable(checked, nextContacts);
  };

  const onToggleContacts = (checked: boolean) => {
    setContactsVisible(checked);
    setSearchable(isSearchable, checked);
  };

  if (loading && !profile) {
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );
  }

  const cvText = formik.values.cvTexts[language] ?? { jobTitle: '', about: '' };
  const photoUrl = localPhotoUrl || (photoValue === '' ? '' : profile?.photoUrl ?? '');
  const state = saving ? 'saving' : failed && dirty ? 'error' : dirty ? 'dirty' : 'saved';

  const preview = (
    <CareerProfileShowcase
      values={formik.values}
      photoUrl={photoUrl}
      headline={cvText.jobTitle}
      about={cvText.about}
    />
  );

  return (
    <Wrapper>
      <FocusBelowToolbar />
      <Header>
        <PageTitle>{t('careerProfile.pageTitle')}</PageTitle>
        <PageInfo>{t('careerProfile.pageInfo')}</PageInfo>
      </Header>

      {isPreview ? null : (
        <Tabs role="tablist" aria-label={t('careerProfile.tabs.label')}>
          {(['cv', 'visibility'] as Tab[]).map((key) => (
            <TabButton
              key={key}
              type="button"
              role="tab"
              id={`cv-tab-${key}`}
              aria-selected={tab === key}
              aria-controls={`cv-panel-${key}`}
              $active={tab === key}
              onClick={() => setTab(key)}>
              {t(`careerProfile.tabs.${key}`)}
            </TabButton>
          ))}
        </Tabs>
      )}

      {tab === 'visibility' ? (
        <Panel id="cv-panel-visibility" role="tabpanel" aria-labelledby="cv-tab-visibility">
          <Card>
            <CardTitle>{t('careerProfile.consent.title')}</CardTitle>
            <CardInfo>{t('careerProfile.consent.info')}</CardInfo>
            <Checkbox
              checked={isSearchable}
              onChange={({ target: { checked } }) => onToggleSearchable(checked)}>
              {t('careerProfile.consent.searchable')}
            </Checkbox>
            <Checkbox
              disabled={!isSearchable}
              checked={contactsVisible}
              onChange={({ target: { checked } }) => onToggleContacts(checked)}>
              {t('careerProfile.consent.contactsVisible')}
            </Checkbox>
          </Card>
        </Panel>
      ) : null}

      {/* 🔴 Вкладка CV прячется, а НЕ размонтируется: Formik живёт выше, но
          поля с собственным состоянием внутри блоков потеряли бы ввод. */}
      <Panel
        id="cv-panel-cv"
        role={isPreview ? undefined : 'tabpanel'}
        aria-labelledby={isPreview ? undefined : 'cv-tab-cv'}
        hidden={tab !== 'cv'}>
        <Toolbar aria-label={t('careerProfile.toolbar.label')}>
          <ToolbarGroup>
            <ToolbarLabel id="cv-language-label">
              {t('careerProfile.toolbar.language')}
            </ToolbarLabel>
            <CvLanguagePicker value={language} onChange={setLanguage} />
          </ToolbarGroup>
          {isPreview ? null : (
            <>
              <SaveState role="status" $state={state}>
                {t(`careerProfile.state.${state}`)}
              </SaveState>
              <ToolbarActions>
                <PrimaryButton
                  type="button"
                  onClick={saveAll}
                  disabled={saving || !dirty}
                  aria-busy={saving}>
                  {t('careerProfile.save')}
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={download}
                  disabled={saving || downloadLoading}
                  aria-busy={downloadLoading}>
                  {t('careerProfile.download.button')}
                </SecondaryButton>
              </ToolbarActions>
            </>
          )}
        </Toolbar>

        <Overview>
          <CvCompleteness checks={journey?.cvChecklist ?? []} onJump={jumpTo} />
          <SectionNav aria-label={t('careerProfile.sections.label')}>
            {CV_SECTIONS.map((section) => (
              <SectionLink key={section.id} type="button" onClick={() => jumpTo(section.id)}>
                {t(section.label)}
              </SectionLink>
            ))}
          </SectionNav>
        </Overview>

        <PaneSwitch role="group" aria-label={t('careerProfile.showcase.modeLabel')}>
          {(['edit', 'preview'] as Pane[]).map((key) => (
            <TabButton
              key={key}
              type="button"
              aria-pressed={pane === key}
              $active={pane === key}
              onClick={() => setPane(key)}>
              {t(
                key === 'edit'
                  ? 'careerProfile.showcase.modeEdit'
                  : 'careerProfile.showcase.modeView',
              )}
            </TabButton>
          ))}
        </PaneSwitch>

        <Workspace>
          {/* Обе панели смонтированы всегда — переключатель лишь прячет одну
              из них на узком экране, и черновик не теряется. */}
          <EditPane $hiddenNarrow={pane !== 'edit'}>
            <CareerProfileForm
              values={formik.values}
              fields={RESUME_FIELDS_RU}
              dateLanguage={Languages.english}
              onChange={formik.setFieldValue}
              readOnly={isPreview}
              skillSuggestions={profile?.cvSkillSuggestions}
              photo={
                isPreview ? null : (
                  <CvPhotoField imageUrl={photoUrl} onChange={(file) => setPhotoValue(file)} />
                )
              }
              cvText={
                <CvTextBlock
                  language={language}
                  value={cvText}
                  readOnly={isPreview}
                  onChange={(field, value) =>
                    formik.setFieldValue(`cvTexts.${language}.${field}`, value)
                  }
                />
              }
            />
          </EditPane>
          <PreviewPane $hiddenNarrow={pane !== 'preview'}>{preview}</PreviewPane>
        </Workspace>
      </Panel>

      {/* Именно проп, а НЕ `{downloadLoading && …}`: показанную модалку нельзя
          размонтировать, иначе она не успеет снять блокировку скролла с body. */}
      <ResumeDownloadModal isShowing={downloadLoading} />
    </Wrapper>
  );
});

/**
 * 0073: фокус не прячется под закреплённой панелью сохранения. Браузер
 * прокручивает к полю с учётом отступа документа — и при Tab, и при переходе
 * по разделам. Только пока открыт редактор CV.
 */
const FocusBelowToolbar = createGlobalStyle`
  html {
    scroll-padding-top: 160px;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding-top: 8px;
  margin-bottom: 80px;
  color: ${careerColor.text};
`;

const Header = styled.header`
  margin: 0 0 24px;
`;

const PageTitle = styled.h1`
  margin: 0;
  ${careerType.h1()};
`;

const PageInfo = styled.p`
  margin: 8px 0 0;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${careerColor.line};
`;

const TabButton = styled.button<{ $active: boolean }>`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => ($active ? careerColor.text : 'transparent')};
  background: transparent;
  color: ${({ $active }) => ($active ? careerColor.text : careerColor.text2)};
  cursor: pointer;
  ${({ $active }) => ($active ? careerType.bodyStrong() : careerType.body())};
`;

const Panel = styled.div`
  width: 100%;
`;

/* Панель сохранения закреплена: кнопка «Zapisz zmiany» видна из любого
   места длинной формы. */
const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
  padding: 12px 0;
  background: ${careerColor.bg};
  border-bottom: 1px solid ${careerColor.line};
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolbarLabel = styled.span`
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const SaveState = styled.span<{ $state: string }>`
  flex: 1 1 auto;
  color: ${({ $state }) =>
    $state === 'error' ? '#c1121f' : $state === 'saved' ? careerColor.done : careerColor.text2};
  ${careerType.meta()};
`;

const ToolbarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${mobile} {
    width: 100%;

    & > button {
      flex: 1 1 auto;
    }
  }
`;

const buttonBase = css`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 20px;
  border-radius: ${careerLayout.controlRadius}px;
  cursor: pointer;
  ${careerType.bodyStrong()};

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase};
  border: 0;
  background: ${Colors.main};
  color: ${CTA_TEXT};

  &:hover:enabled {
    background: ${Colors.main_dark};
  }
`;

const SecondaryButton = styled.button`
  ${buttonBase};
  border: 1px solid ${careerColor.line};
  background: transparent;
  color: ${careerColor.text};

  &:hover:enabled {
    background: ${careerColor.hover};
  }
`;

const Overview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
`;

const SectionNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
`;

const SectionLink = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.meta()};

  &:hover {
    background: ${careerColor.hover};
  }
`;

const PaneSwitch = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${careerColor.line};

  ${wide} {
    display: none;
  }
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;

  ${wide} {
    grid-template-columns: minmax(0, 52fr) minmax(0, 48fr);
    align-items: start;
  }
`;

const EditPane = styled.div<{ $hiddenNarrow: boolean }>`
  min-width: 0;
  display: ${({ $hiddenNarrow }) => ($hiddenNarrow ? 'none' : 'block')};

  ${wide} {
    display: block;
  }
`;

const PreviewPane = styled.div<{ $hiddenNarrow: boolean }>`
  min-width: 0;
  display: ${({ $hiddenNarrow }) => ($hiddenNarrow ? 'none' : 'block')};

  ${wide} {
    display: block;
    position: sticky;
    top: 88px;
    max-height: calc(100vh - 104px);
    overflow: auto;
  }
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 24px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.cardRadius}px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardTitle = styled.h2`
  margin: 0;
  ${careerType.h2()};
`;

const CardInfo = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
